import {
  Component,
  ElementRef,
  inject,
  Inject,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/shared/services/api.service';

@Component({
  selector: 'app-order-status-attachments',
  templateUrl: './order-status-attachments.component.html',
  styleUrl: './order-status-attachments.component.scss',
  standalone: false
})
export class OrderStatusAttachmentsComponent {
  id: number;
  dataChanged = false;
  files: ExtendedFile[] = [];
  uploadedFiles: ExtendedFile[] = [];
  attachments: OrderStatusAttachment[] = [];
  message = '';
  showMessage = false;
  receivedData: boolean = false;
  backgroundColor = 'orange';
  displayedColumns: string[] = ['name', 'created', 'actions'];
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;

  private _snackBar = inject(MatSnackBar);

  constructor(
    public dialogRef: MatDialogRef<OrderStatusAttachmentsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _api: ApiService,
  ) {}

  ngOnInit() {
    if (this.data) {
      this.id = this.data.id;
      this._api.OrderStatus.GetOrderStatusAttachments(this.id).subscribe((x) => {
        if (x.Attachments.length > 0) {
          this.receivedData = true;
        }
        this.attachments = x.Attachments;
        this.files = this.convertAttachmentsToFiles(x.Attachments);
      });
    }
  }
  onFileDropped(files: FileList) {
    this.attachUniqueFile(files);
  }

  onFileSelected(event: any) {
    const incomingFiles: FileList = event.target.files;
    this.attachUniqueFile(incomingFiles);
  }

  attachUniqueFile(incomingFiles: FileList) {
    let fileListArr: ExtendedFile[] = Array.from(
      incomingFiles,
    ) as ExtendedFile[];
    if (fileListArr.length < 0) return;

    let filesAdded = false;
    fileListArr.map((incomingFile) => {
      const isFilePresent = this.files.some(
        (file) => file.name === incomingFile.name,
      );
      if (isFilePresent) {
        alert(`File ${incomingFile.name} is already present in attachments`);
      } else {
        if (incomingFile.size > 10 * 1024 * 1024) {
          // file size should not exceed 10mb
          alert(`File ${incomingFile.name} should not exceed 10mb`);
        } else {
          this.files.push(incomingFile);
          this.uploadedFiles.push(incomingFile);
          filesAdded = true;
        }
      }
    });
    if (filesAdded) {
      this.saveFiles();
      this.clearFileInput();
    }
  }

  saveFiles() {
    if (this.uploadedFiles.length >= 0) {
      this.message = 'Uploading files...';
      this.backgroundColor = 'orange';
      this.showMessage = true;
      this._api.OrderStatus.UploadOrderStatusAttachments(
        this.id,
        this.uploadedFiles,
      ).subscribe({
        next: (res) => {
          this.uploadedFiles = [];
          // Success message
          this.message = 'Files uploaded successfully';
          this.backgroundColor = 'green';
          this._api.OrderStatus.GetOrderStatusAttachments(this.id).subscribe(
            (x) => {
              const newFiles = this.convertAttachmentsToFiles(x.Attachments);
              this.files = newFiles;
              this.attachments = x.Attachments;
              this.receivedData = true;
            },
          );
        },
        error: (err) => {
          console.error('Failed to save files', err);
          this.message = 'Failed to upload files';
          this.backgroundColor = 'red';
        },
        complete: () => {
          setTimeout(() => {
            this.showMessage = false;
            this.backgroundColor = 'orange';
          }, 1000);
        },
      });
    }
  }

  clearFileInput() {
    // Get the file input element
    const inputElement = this.fileInput.nativeElement as HTMLInputElement;
    // Clear the selected file by setting the value to an empty string
    inputElement.value = '';
  }

  convertAttachmentsToFiles(
    attachments: OrderStatusAttachment[],
  ): ExtendedFile[] {
    const files: any[] = [];
    if (attachments.length <= 0) return [];
    for (const attachment of attachments) {
      // Create a new File object
      const blob = new Blob([], { type: 'application/' }); // You can specify the file type
      const file = new File([blob], attachment.FileName, {
        type: 'application/',
      });

      // Set the file's size property
      Object.defineProperty(file, 'size', { value: attachment.Size });
      Object.defineProperty(file, 'id', { value: attachment.Id });
      Object.defineProperty(file, 'feedbackId', {
        value: attachment.OrderStatusItemId,
      });
      Object.defineProperty(file, 'created', {
        value: attachment.Created,
      });
      Object.defineProperty(file, 'userName', {
        value: attachment.UserName,
      });

      files.push(file);
    }

    return files;
  }

  downloadAttachment(file: ExtendedFile): void {
    if (!file || !file.name || !file.id) return;
    const attachment: OrderStatusAttachment = {
      Id: file.id,
      FileName: file.name,
      Size: file.size,
      UserName: file.userName,
      Created: file.created,
      OrderStatusItemId: file.orderStatusId,
    };
    this.message = 'Downloading started...';
    this.backgroundColor = 'orange';
    this.showMessage = true;
    this._api.OrderStatus.DownloadOrderStatusAttachment(
      attachment,
      (err: string) => {
        if (err) {
          console.error('Download failed:', err);
          this.message = 'Failed to download file';
          this.backgroundColor = 'red';
          this.showMessage = true;
          setTimeout(() => (this.showMessage = false), 2000);
        } else {
          this.message = 'Download complete';
          this.backgroundColor = 'green';
          this.showMessage = false;
        }
      },
    );
  }

  deleteAttachment(id: number): void {
    if (!id) return;

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this attachment?',
    );
    if (!confirmDelete) return;
    this._api.OrderStatus.DeleteOrderStatusAttachment(id).subscribe({
      next: () => {
        this.message = 'Attachment deleted successfully';
        this.backgroundColor = 'green';
        this.showMessage = true;

        this._api.OrderStatus.GetOrderStatusAttachments(this.id).subscribe((x) => {
          if (x.Attachments.length <= 0) {
            this.receivedData = true;
          }
          this.files = this.convertAttachmentsToFiles(x.Attachments);
          this.attachments = x.Attachments;
        });
      },

      error: (err) => {
        console.error('Delete failed', err);
        this.message = 'Failed to delete attachment';
        this.backgroundColor = 'red';
        this.showMessage = true;
      },

      complete: () => {
        setTimeout(() => {
          this.showMessage = false;
          this.backgroundColor = 'orange';
        }, 1000);
      },
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const utcDateString = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action);
  }

  closeDialog() {
    this.dialogRef.close({ dataChanged: this.dataChanged });
  }
}

type ExtendedFile = File & {
  id: number;
  orderStatusId: number;
  fileName: string;
  created: string;
  userName: string;
  size: number;
};

export class OrderStatusAttachment {
  Id: number = 0;
  OrderStatusItemId: number = 0;
  FileName: string = '';
  Size: number = 0;
  Created: string = '';
  UserName: string = '';
}
